import React, { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { get } from 'lodash';
import { useSweeps } from '../../hooks/useSweeps';
import { useAuth } from '../../hooks/useAuth';
import { getEntryBySweepId } from '../../api/sweepstakeApi';
import { Layout } from '../../components/Layout';
import Loading from '../../components/Loading';
import { SweepCard } from '../../components/SweepCard';
import { isPick7iOSWebView } from '../../utils/authUtils';
import { sendMetadataEvent } from '../../helpers/googleAnalyticsHelpers/gaHelper';
import GoogleAnalyticsMetaDataObj from '../../helpers/googleAnalyticsHelpers/buildGoogleAnalyticsMetaDataObject';
import * as Styled from './styles';

export const HubPage = () => {
    const { sweeps, loading, error } = useSweeps();
    const { authStore } = useAuth();
    const [entries, setEntries] = useState({});
    const [entriesLoading, setEntriesLoading] = useState(true);
    const [searchParams] = useSearchParams();

    const customerId = get(authStore, 'user.uki_customerid', null);
    const loggedIn = get(authStore, 'loggedIn', false);
    const regStatus = get(authStore, 'user.com.sbgcore.upgradable', null);

    useEffect(() => {
        const googleAnalyticsMetadata = new GoogleAnalyticsMetaDataObj(
            customerId,
            loggedIn,
            regStatus
        );
        sendMetadataEvent(googleAnalyticsMetadata, 'Sweepstake');
    }, []);

    useEffect(() => {
        if (!authStore.loggedIn || !sweeps.length) {
            setEntriesLoading(false);
            return undefined;
        }

        const currentSweeps = sweeps;
        setEntriesLoading(true);

        const fetchEntries = async () => {
            const entryMap = {};
            await Promise.all(
                currentSweeps.map(async sweep => {
                    try {
                        const { data } = await getEntryBySweepId({
                            sweepId: sweep.sweepId,
                            productToken: authStore.productToken
                        });
                        if (data) {
                            entryMap[sweep.sweepId] = data;
                        }
                    } catch (err) {
                        // Log but don't fail entire fetch
                        // eslint-disable-next-line no-console
                        console.error(
                            `Failed to fetch entry for ${sweep.sweepId}`,
                            err
                        );
                    }
                })
            );
            setEntries(entryMap);
            setEntriesLoading(false);
        };

        return fetchEntries();
    }, [authStore.loggedIn, authStore.productToken, sweeps]);

    if (isPick7iOSWebView() && searchParams.has('productToken')) {
        return (
            <Navigate
                to={`/sso/loginuser?${searchParams.toString()}`}
                replace
            />
        );
    }

    if (loading || entriesLoading) {
        return (
            <Layout
                loggedIn={authStore.loggedIn}
                userName={authStore.user.given_name}
            >
                <Loading heading="LOADING" />
            </Layout>
        );
    }

    return (
        <Layout
            loggedIn={authStore.loggedIn}
            userName={authStore.user.given_name}
        >
            <Styled.HubWrapper>
                {error && (
                    <Styled.ErrorText>
                        Unable to load sweepstakes. Please try again later.
                    </Styled.ErrorText>
                )}
                {!error && sweeps.length === 0 && (
                    <Styled.EmptyText>
                        There are no sweepstakes available right now. Check back
                        soon!
                    </Styled.EmptyText>
                )}
                <Styled.CardGrid>
                    {sweeps.map(sweep => {
                        const entryData = entries[sweep.sweepId];
                        return (
                            <SweepCard
                                key={sweep.sweepId}
                                sweep={sweep}
                                entry={entryData}
                            />
                        );
                    })}
                </Styled.CardGrid>
            </Styled.HubWrapper>
        </Layout>
    );
};

export default HubPage;
